import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Alert, Checkbox, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import api from '../../services/api.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

/* ───── כל ה-resources וה-actions של המערכת ───── */
const ALL_RESOURCES = [
  { key: 'members',           label: 'חברים' },
  { key: 'exams',             label: 'מבחנים' },
  { key: 'examRegistration',  label: 'הרשמות למבחנים' },
  { key: 'grades',            label: 'ציונים' },
  { key: 'messages',          label: 'הודעות' },
  { key: 'kolels',            label: 'כוללים' },
  { key: 'drashas',           label: 'דרשות' },
  { key: 'subscriptions',     label: 'מנויים' },
  { key: 'reports',           label: 'דוחות' },
  { key: 'auditLogs',         label: 'יומן ביקורת' },
  { key: 'featureFlags',      label: 'Feature Flags' },
  { key: 'emailDomains',      label: 'דומיין לשליחת מיילים' },
  { key: 'roles',             label: 'תפקידים' },
  { key: 'users',             label: 'משתמשים' },
  { key: 'narrationPrompts',  label: 'קטלוג קריינות' },
  { key: 'ivr',               label: 'IVR / ימות' },
  { key: 'examCity',          label: 'ערי מבחן' },
  { key: 'examPeriod',        label: 'זמני מבחנים' },
  { key: 'labels',            label: 'מדבקות וברקודים' },
  { key: 'studyCalendar',     label: 'לוח שנה' },
];

const ALL_ACTIONS = ['read', 'create', 'update', 'delete'];
const ACTION_LABELS = { read: 'קריאה', create: 'יצירה', update: 'עדכון', delete: 'מחיקה' };

/* ───── עזרים: המרה בין מערך permissions ↔ מפת צ׳קבוקסים ───── */
const permissionsToMap = (permissions = []) => {
  const map = {};
  for (const p of permissions) {
    if (!map[p.resource]) map[p.resource] = {};
    for (const a of p.actions) {
      map[p.resource][a] = true;
    }
  }
  return map;
};

const mapToPermissions = (map) => {
  const perms = [];
  for (const resource of Object.keys(map)) {
    const actions = Object.keys(map[resource]).filter((a) => map[resource][a]);
    if (actions.length > 0) {
      perms.push({
        resource,
        actions,
        fieldRestrictions: { denied: [], readOnly: [] },
      });
    }
  }
  return perms;
};

/* ───── סיכום הרשאות לתצוגה ───── */
const PermsSummary = ({ permissions = [] }) => {
  if (permissions.length === 0) {
    return <Typography variant="body2" color="text.secondary">ללא הרשאות</Typography>;
  }

  const full = permissions.filter((p) => p.actions.length === 4);
  const partial = permissions.filter((p) => p.actions.length > 0 && p.actions.length < 4);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {full.map((p) => {
        const label = ALL_RESOURCES.find((r) => r.key === p.resource)?.label || p.resource;
        return (
          <Chip key={p.resource} label={label} size="small" color="primary" variant="filled"
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: 12, height: 24 }}
          />
        );
      })}
      {partial.map((p) => {
        const label = ALL_RESOURCES.find((r) => r.key === p.resource)?.label || p.resource;
        const actionsText = p.actions.map((a) => ACTION_LABELS[a] || a).join(', ');
        return (
          <Tooltip key={p.resource} title={actionsText} arrow>
            <Chip label={label} size="small" color="default" variant="outlined"
              sx={{ fontSize: 12, height: 24 }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

/* ═══════════════════════════════════════════════ */

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [permMap, setPermMap] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/roles');
      setRoles(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRoles(); }, []);
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }
  }, [success]);

  /* ── פתיחת דיאלוג ── */
  const openNew = () => {
    setEditRole(null);
    setForm({ name: '', description: '' });
    setPermMap({});
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditRole(role);
    setForm({ name: role.name, description: role.description });
    setPermMap(permissionsToMap(role.permissions));
    setError('');
    setDialogOpen(true);
  };

  /* ── toggle צ׳קבוקס בודד ── */
  const togglePerm = (resource, action) => {
    setPermMap((prev) => {
      const next = { ...prev };
      if (!next[resource]) next[resource] = {};
      next[resource] = { ...next[resource], [action]: !next[resource][action] };
      return next;
    });
  };

  /* ── toggle שורה שלמה ── */
  const toggleRow = (resource) => {
    setPermMap((prev) => {
      const current = prev[resource] || {};
      const allChecked = ALL_ACTIONS.every((a) => current[a]);
      const next = { ...prev };
      next[resource] = {};
      for (const a of ALL_ACTIONS) {
        next[resource][a] = !allChecked;
      }
      return next;
    });
  };

  /* ── toggle עמודה שלמה ── */
  const toggleColumn = (action) => {
    setPermMap((prev) => {
      const allChecked = ALL_RESOURCES.every((r) => prev[r.key]?.[action]);
      const next = { ...prev };
      for (const r of ALL_RESOURCES) {
        if (!next[r.key]) next[r.key] = {};
        next[r.key] = { ...next[r.key], [action]: !allChecked };
      }
      return next;
    });
  };

  /* ── select / deselect all ── */
  const selectAll = () => {
    const map = {};
    for (const r of ALL_RESOURCES) {
      map[r.key] = {};
      for (const a of ALL_ACTIONS) map[r.key][a] = true;
    }
    setPermMap(map);
  };
  const deselectAll = () => setPermMap({});

  /* ── שמירה ── */
  const handleSave = async () => {
    setError('');
    const payload = {
      name: form.name,
      description: form.description,
      permissions: mapToPermissions(permMap),
    };
    try {
      if (editRole) {
        await api.put(`/roles/${editRole._id}`, payload);
        setSuccess('התפקיד עודכן בהצלחה');
      } else {
        await api.post('/roles', payload);
        setSuccess('התפקיד נוצר בהצלחה');
      }
      setDialogOpen(false);
      loadRoles();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה');
    }
  };

  const handleDelete = async (id) => {
    if (!(await ckConfirm({ title: 'מחיקת תפקיד', message: 'למחוק תפקיד?', confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/roles/${id}`);
      setSuccess('התפקיד נמחק');
      loadRoles();
    } catch (err) {
      console.error(err);
    }
  };

  /* ── חישוב סטטוס צ׳קבוקסים ── */
  const isChecked = (resource, action) => !!permMap[resource]?.[action];

  const isRowAllChecked = (resource) => ALL_ACTIONS.every((a) => permMap[resource]?.[a]);
  const isRowIndeterminate = (resource) => {
    const checked = ALL_ACTIONS.filter((a) => permMap[resource]?.[a]).length;
    return checked > 0 && checked < ALL_ACTIONS.length;
  };

  const isColAllChecked = (action) => ALL_RESOURCES.every((r) => permMap[r.key]?.[action]);
  const isColIndeterminate = (action) => {
    const checked = ALL_RESOURCES.filter((r) => permMap[r.key]?.[action]).length;
    return checked > 0 && checked < ALL_RESOURCES.length;
  };

  const totalChecked = useMemo(() => {
    let count = 0;
    for (const r of ALL_RESOURCES) {
      for (const a of ALL_ACTIONS) {
        if (permMap[r.key]?.[a]) count++;
      }
    }
    return count;
  }, [permMap]);

  if (loading) return <Box><Typography>טוען...</Typography></Box>;

  return (
    <Box>
      {/* Header */}
      <AdminPageHeader
        title="תפקידים והרשאות"
        sub="מה כל תפקיד רשאי לראות ולערוך במערכת"
        primary={{ label: 'תפקיד חדש', onClick: openNew }}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>תפקידים והרשאות</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              תפקיד חדש
            </Button>
          </Box>
        )}
      />

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Roles table */}
      <Paper sx={{ overflow: 'auto' }}>
        <table className="tablekit-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead className="tablekit-thead">
            <tr>
              <th className="tablekit-th">שם</th>
              <th className="tablekit-th">תיאור</th>
              <th className="tablekit-th">הרשאות</th>
              <th className="tablekit-th" style={{ width: 100 }}>פעולות</th>
            </tr>
          </thead>
          <tbody className="tablekit-tbody">
            {roles.length === 0 && (
              <tr>
                <td className="tablekit-td" colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--ck-text-3, #999)' }}>
                  אין תפקידים
                </td>
              </tr>
            )}
            {roles.map((role) => (
              <tr key={role._id} className="tablekit-tr">
                <td className="tablekit-td" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{role.name}</td>
                <td className="tablekit-td">{role.description}</td>
                <td className="tablekit-td">
                  <PermsSummary permissions={role.permissions} />
                </td>
                <td className="tablekit-td">
                  <Tooltip title="ערוך">
                    <IconButton size="small" onClick={() => openEdit(role)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחק">
                    <IconButton size="small" color="error" onClick={() => handleDelete(role._id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>

      {/* ═══ Dialog — עריכה / יצירה ═══ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editRole ? `עריכת תפקיד — ${editRole.name}` : 'תפקיד חדש'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Name + Description */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="שם התפקיד" value={form.name} autoFocus
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              label="תיאור" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              sx={{ flex: 2 }}
            />
          </Box>

          {/* Permissions matrix */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              הרשאות
              <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                ({totalChecked} / {ALL_RESOURCES.length * ALL_ACTIONS.length} נבחרו)
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={selectAll}>סמן הכל</Button>
              <Button size="small" variant="outlined" color="inherit" onClick={deselectAll}>נקה הכל</Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'var(--ck-surface-2, #f5f5f5)', position: 'sticky', right: 0, zIndex: 3, minWidth: 130 }}>
                    משאב
                  </TableCell>
                  {ALL_ACTIONS.map((action) => (
                    <TableCell key={action} align="center"
                      sx={{ fontWeight: 700, bgcolor: 'var(--ck-surface-2, #f5f5f5)', minWidth: 80 }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" fontWeight={700}>{ACTION_LABELS[action]}</Typography>
                        <Checkbox
                          size="small"
                          checked={isColAllChecked(action)}
                          indeterminate={isColIndeterminate(action)}
                          onChange={() => toggleColumn(action)}
                          sx={{ p: 0, mt: 0.3 }}
                        />
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'var(--ck-surface-2, #f5f5f5)', minWidth: 60 }}>
                    <Typography variant="caption" fontWeight={700}>הכל</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ALL_RESOURCES.map((res) => (
                  <TableRow key={res.key} hover>
                    <TableCell sx={{ fontWeight: 500, position: 'sticky', right: 0, bgcolor: 'var(--ck-surface, #fff)', zIndex: 1 }}>
                      {res.label}
                    </TableCell>
                    {ALL_ACTIONS.map((action) => (
                      <TableCell key={action} align="center" sx={{ p: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={isChecked(res.key, action)}
                          onChange={() => togglePerm(res.key, action)}
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ p: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={isRowAllChecked(res.key)}
                        indeterminate={isRowIndeterminate(res.key)}
                        onChange={() => toggleRow(res.key)}
                        sx={{ p: 0 }}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editRole ? 'עדכן' : 'צור תפקיד'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;
